import React, { useEffect, useState } from 'react'
import { ErrorMessage } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'react-bootstrap'
import {
  MAX_FILE_SIZE,
  MAX_PDF_SIZE,
  TLDS,
  MAX_FILE_SIZE_FREE,
  MIXED_FOLDER_ERROR_MSG,
  MIXED_ZIP_ERROR_MSG,
  DROPZONE_ACCEPTED_FILES,
  DROPZONE_ACCEPTED_FILES_PRO
} from '../../../../constants'
import ContentSelector from 'components/ContentSelector/ContentSelector'
import {
  createFileInput,
  getDropzoneAcceptedFiles,
  getTLDs,
  isPDF,
  isTotalSizeWithinLimit,
  isValidFileSize,
  validateFilesSelection,
  isWasmerFile,
  getDomainSuffix,
  onUpgradePlan
} from 'utils/general'
import { promptForAdditionalFiles, removeFileFromUploadState } from 'utils/file-upload'
import { PRO_MAX_FILE_SIZES } from 'constants/plans'
import { showUpgradeCardModal } from 'actions'
import {
  findHtmlFilesInZipFile,
  getFileContentType,
  getFileType,
  isIndexFile,
  getPhpFiles,
  getPythonFiles,
  getNodeFiles,
  isHtmlFile,
  determineHtmlIndexFile,
  isHtmlFileExisting
} from 'helpers/file-type'
import FileListModal from './modals/FileListModal'
import { uploadedFileTooBig } from 'utils/sendy'
import { useFormikContext } from 'formik'
import SelectedFile from '../../../SelectedFile/SelectedFile'
import { isZipFile } from '../../interface'
import { setDraggedFile } from 'Manage/actions'
import { isFolderDrop, createMockFolderFile } from 'utils/folder-upload'
import toast from 'react-hot-toast'

/**
 * @todo: Uploading Pdf or Html file then upload zip show upgrade modal, maybe it should display something else ?
 * @todo: Can rework the plain javascript on adding file
 */

const FileSelectionForm = ({ name, initialValues, domainValues, userProfile, setFieldValue, onShowCodeEditor }) => {
  const dispatch = useDispatch()
  const draggedFile = useSelector((state) => state.manage.draggedFile)
  const [showFileList, setShowFileList] = useState(false)
  const [htmlFiles, setHtmlFiles] = useState([])
  const [serverFiles, setServerFiles] = useState([])

  const { selectedFile, addedFiles, fileUploadLimit, indexFile } = initialValues
  const { domain } = domainValues
  const { productId } = userProfile

  const [acceptedFiles, setAcceptedFiles] = useState(
    getDropzoneAcceptedFiles(getFileContentType(selectedFile?.name) || selectedFile?.fileType || selectedFile?.type)
  )

  const { validateForm } = useFormikContext()
  useEffect(() => {
    if (draggedFile) {
      const files = Array.isArray(draggedFile) ? draggedFile : [draggedFile]
      const isReplacement = files.some((f) => f?.isFolder)
      onFileSelected(files, isReplacement).then(() => {
        setFieldValue(`${name}.draggedFile`, null)
        dispatch(setDraggedFile(null))
      })
    }
  }, [draggedFile])

  const zipFileUploaded = async (file) => {
    const filesFound = await findHtmlFilesInZipFile(file)
    const { containsPhpFile, phpFiles } = await getPhpFiles(file)
    const { containsPythonFile, pythonFiles } = await getPythonFiles(file)
    const { containsNodeFile, isReact, nodeFiles } = await getNodeFiles(file)

    if (containsNodeFile || isReact) {
      file.isNodeOrReactZip = true
      setFieldValue(`${name}.selectedFile`, file)
      setFieldValue(`domainValues.domainSuffix`, TLDS.TIINY_IO)
      setFieldValue(`domainValues.tlds`, [TLDS.TIINY_IO])
      setServerFiles([])
      setHtmlFiles([])
      setShowFileList(false)
    } else if ((containsPhpFile && phpFiles?.length > 0) || (containsPythonFile && pythonFiles?.length > 0)) {
      // PHP or Python files exist - always show modal to let user choose
      setServerFiles(containsPhpFile ? phpFiles : pythonFiles)
      setHtmlFiles([])
      setShowFileList(true)
      // Don't set index - let user choose from modal
    } else {
      // Only HTML files - use smart logic
      const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(
        filesFound.map((f) => ({ name: f, path: f }))
      )

      setHtmlFiles(htmlFilesFound)
      setServerFiles([])
      setShowFileList(shouldShowModal)

      // Auto-set index if determined (single file or index.html exists)
      if (indexFileToSet) {
        setFieldValue(`${name}.indexFile`, indexFileToSet)
      }
    }
  }

  const isNewHtmlFilesUploaded = (files) => {
    // Only process if there are HTML files
    if (!isHtmlFileExisting(files)) {
      return
    }

    const { htmlFilesFound, shouldShowModal, indexFileToSet } = determineHtmlIndexFile(files)

    // Always update HTML files list
    setHtmlFiles(htmlFilesFound)

    // Show modal if needed
    if (shouldShowModal) {
      setShowFileList(true)
    }

    // Set index file if determined
    if (indexFileToSet) {
      setFieldValue(`${name}.indexFile`, indexFileToSet)
    } else if (htmlFilesFound.length === 0) {
      // Clear if no HTML files
      setFieldValue(`${name}.indexFile`, null)
    }
  }

  const onFileSelected = async (passedFiles, isReplacement = false) => {
    let files = passedFiles

    if (isFolderDrop(passedFiles)) {
      try {
        const zipFile = createMockFolderFile(passedFiles)
        files = [zipFile]
      } catch (error) {
        toast.error('Error processing the folder. Please try again.')
        return
      }
    }

    if (files.some((file) => file.path?.startsWith('/'))) {
      files = files.filter((file) => !file.path?.startsWith('/'))
    }

    const isTrial = !productId
    const maxSizeMB = isTrial ? MAX_FILE_SIZE_FREE : fileUploadLimit || PRO_MAX_FILE_SIZES[productId]

    const allFiles = selectedFile && !isReplacement ? [selectedFile, ...addedFiles, ...files] : files
    if (allFiles.some((file) => isZipFile(file?.type)) && allFiles.length > 1) {
      if (allFiles.some((file) => file && file.isFolder)) {
        toast.error(MIXED_FOLDER_ERROR_MSG)
      } else {
        toast.error(MIXED_ZIP_ERROR_MSG)
      }
      return
    }

    for (const file of files) {
      if (!isValidFileSize(file, maxSizeMB)) {
        dispatch(
          showUpgradeCardModal({
            title: 'Upgrade to upload larger files',
            desc: `Your current plan only allows you to upload ${isPDF(file) ? 'PDFs' : 'files'} less than ${
              isPDF(file) && isTrial ? MAX_PDF_SIZE : maxSizeMB || MAX_FILE_SIZE
            }MB. File "${file.name}" is too large.`,
            cta: 'Upload larger files',
            onClick: file?.size ? () => onUpgradePlan({ fileSize: file?.size, userProfile }, dispatch) : null
          })
        )
        uploadedFileTooBig()
        return
      }
    }

    if (selectedFile && !isReplacement) {
      const validFiles = []

      for (const file of files) {
        if (!validateFilesSelection([selectedFile, ...addedFiles, ...validFiles], file)) {
          alert('Please upload files with different names')
          return
        }

        if (isTotalSizeWithinLimit(file, [selectedFile, ...addedFiles, ...validFiles], maxSizeMB)) {
          validFiles.push(file)
        } else {
          dispatch(
            showUpgradeCardModal({
              title: 'Upgrade to upload larger files',
              desc: `Total files should be less than ${maxSizeMB}MB on your current plan. Upgrade for more.`,
              cta: 'Upload larger files',
              onClick: file?.size ? () => onUpgradePlan({ fileSize: file?.size, userProfile }, dispatch) : null
            })
          )
          uploadedFileTooBig()
          return
        }
      }

      if (validFiles.length > 0) {
        const updatedFiles = [...addedFiles, ...validFiles]
        setFieldValue(`${name}.addedFiles`, updatedFiles)
        isNewHtmlFilesUploaded([selectedFile, ...updatedFiles])
      }
      return
    } else {
      const mainFile = files[0]

      if (isZipFile(mainFile.type)) {
        await zipFileUploaded(mainFile)
      } else if (isWasmerFile(mainFile)) {
        setFieldValue(`domainValues.domainSuffix`, TLDS.TIINY_IO)
        setFieldValue(`domainValues.tlds`, [TLDS.TIINY_IO])
      }

      isNewHtmlFilesUploaded(files)

      setFieldValue(`${name}.selectedFile`, mainFile)
      setFieldValue(`${name}.fileType`, getFileType(mainFile.name))
      setAcceptedFiles(mainFile.type)

      setFieldValue(`${name}.addedFiles`, files.slice(1))
    }
  }

  const addMoreFiles = () => {
    const maxSizeMB = fileUploadLimit || PRO_MAX_FILE_SIZES[productId] || MAX_FILE_SIZE

    promptForAdditionalFiles({
      files: addedFiles,
      fileSelected: selectedFile,
      acceptedFilesType: DROPZONE_ACCEPTED_FILES_PRO,
      onFilesValidated: (validFilesToAdd) => {
        const updatedFiles = [...addedFiles, ...validFilesToAdd]
        setFieldValue(`${name}.addedFiles`, updatedFiles)
        isNewHtmlFilesUploaded([selectedFile, ...updatedFiles])
      },
      customValidation: (fileToAdd, allExistingFiles) => {
        const isTrial = !productId

        if (
          !validateFilesSelection(
            allExistingFiles.filter((f) => f.name !== fileToAdd.name),
            fileToAdd
          )
        ) {
          alert('Please upload files with different names')
          return false
        }

        if (
          !isTotalSizeWithinLimit(
            fileToAdd,
            allExistingFiles.filter((f) => f.name !== fileToAdd.name),
            maxSizeMB
          )
        ) {
          dispatch(
            showUpgradeCardModal({
              title: 'Upgrade to upload larger files',
              desc: `Total files should be less than ${maxSizeMB}MB on your current plan. Upgrade for more.`,
              cta: 'Upload larger files',
              onClick: fileToAdd?.size
                ? () => onUpgradePlan({ fileSize: fileToAdd?.size, userProfile }, dispatch)
                : null
            })
          )
          uploadedFileTooBig()
          return false
        }

        return true
      }
    })
  }

  const handleSetAsIndex = (fileName) => {
    setFieldValue(`${name}.indexFile`, fileName)
  }

  const onRemove = (fileToRemove, isSelectedMainFile) => {
    removeFileFromUploadState({
      files: addedFiles,
      fileSelected: selectedFile,
      fileToRemove,
      isSelectedFile: isSelectedMainFile,
      onUpdateFiles: ({ newSelectedFile, newFiles }) => {
        if (!newSelectedFile && newFiles.length === 0) {
          const tlds = getTLDs({ account: userProfile })
          setFieldValue(`${name}.fileType`, null)
          setFieldValue(`${name}.selectedFile`, null)
          setFieldValue(`${name}.indexFile`, null)
          setFieldValue(`${name}.isZipPhpFile`, false)

          setFieldValue(`domainValues.tlds`, tlds)
          setFieldValue(`domainValues.domainSuffix`, getDomainSuffix(domain).tld || tlds[0])

          setHtmlFiles([])
          setServerFiles([])
          setAcceptedFiles(getDropzoneAcceptedFiles())
        } else {
          setFieldValue(`${name}.fileType`, getFileType(newSelectedFile?.name))
          setFieldValue(`${name}.selectedFile`, newSelectedFile)
          setFieldValue(`${name}.addedFiles`, newFiles)
          setAcceptedFiles(
            getDropzoneAcceptedFiles(
              getFileContentType(newSelectedFile?.name) || newSelectedFile?.type || newSelectedFile?.fileType
            )
          )
          isNewHtmlFilesUploaded([newSelectedFile, ...newFiles])
        }
      }
    })
  }

  return (
    <>
      {selectedFile ? (
        <div className="mb-3">
          {/* SHOW UPLOADED FILES */}
          <div className={`${addedFiles.length > 0 ? 'file-list' : ''}`}>
            <SelectedFile
              className="justify-center"
              file={selectedFile || draggedFile}
              indexFile={indexFile}
              onRemove={() => onRemove(selectedFile || draggedFile, true)}
              onSetAsIndex={isHtmlFile(selectedFile?.name) ? handleSetAsIndex : null}
              hideHomepageLabel={addedFiles.length === 1}
            />

            {addedFiles?.map((file, index) => (
              <SelectedFile
                key={index}
                file={file}
                indexFile={indexFile}
                onRemove={() => onRemove(file, false)}
                onSetAsIndex={isHtmlFile(file?.name) ? handleSetAsIndex : null}
                hideHomepageLabel={addedFiles.length === 1}
              />
            ))}
          </div>
          {/* END SHOW UPLOADED FILES */}

          {/* ADD FILES */}
          {!isZipFile(selectedFile?.type || selectedFile?.fileType) &&
            !isWasmerFile(selectedFile) &&
            ![selectedFile, ...(addedFiles || [])].some((f) => f?.isFolder) && (
              <Button
                className="mt-2 ms-auto d-block w-100"
                variant="outline-primary"
                size="sm"
                onClick={(event) => addMoreFiles(event)}
                data-testid="FileSelectionForm-add-more-files"
              >
                + Add more files
              </Button>
            )}
          {/* END ADD FILES */}
        </div>
      ) : (
        <>
          {/* DROPZONE */}
          <ContentSelector
            className="tr-landing-upload-file-zone mb-3"
            onFileSelect={onFileSelected}
            onTemplateSelected={(template) => {
              setFieldValue(`${name}.selectedFile`, template)
              setFieldValue(`${name}.fileType`, getFileType(template.path))
              validateForm()
            }}
            acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}
            onShowCodeEditor={onShowCodeEditor}
            hideTabs
          />
          {/* END DROPZONE */}
        </>
      )}
      <ErrorMessage name={`${name}.selectedFile`} component="div" className="validation-error text-center" />

      {/** CONSOLIDATED FILE PICKER - handles both PHP and HTML intelligently */}
      {(serverFiles?.length > 0 || htmlFiles?.length > 0) && (
        <FileListModal
          show={showFileList}
          onHide={() => setShowFileList(false)}
          onFileSelected={(file) => {
            setFieldValue(`${name}.indexFile`, file)

            if (isWasmerFile(file)) {
              setFieldValue(`${name}.isZipPhpFile`, true)
              setFieldValue(`domainValues.domainSuffix`, TLDS.TIINY_IO)
              setFieldValue(`domainValues.tlds`, [TLDS.TIINY_IO])
            }
            setShowFileList(false)
          }}
          filesList={serverFiles?.length > 0 ? serverFiles : htmlFiles}
          indexFile={indexFile}
          title="Which file should be your homepage?"
          subtitle={
            serverFiles?.length > 0
              ? 'Select your main file (PHP/Python files will use .tiiny.io domain)'
              : "We've found multiple HTML files in your Zip"
          }
        />
      )}
      {/** END OF CONSOLIDATED FILE PICKER */}
    </>
  )
}

export default FileSelectionForm
